import { createSelector } from '@reduxjs/toolkit';

import { Auth } from 'types/Auth';
import { UnpaidInvoice } from 'types/Insight';
import { Sale, SaleLine, SubscriptionInfo } from 'types/Sale';
import { UserWallet } from 'types/Wallet';
import { SALE_MAP, TXN_MAP, AUTOPAY_MAP, SURCHARGE_FF_KEY } from 'types/constants';
const { SUBSCRIPTION_STATUS_TYPES } = SALE_MAP;

export const saleSelectors = {
  isEditAmountDisabledSelector(sale: Sale) {
    return sale?.isEditAmountDisabled;
  },
  domainIdSelector(sale: Sale, auth: Auth): string | void {
    if (auth && auth.realmId) {
      return `INVOICE:${auth.realmId}_${localIdSelector(sale)}`;
    }
  },
  localIdSelector,
  gratuityEnabledSelector(sale: Sale) {
    return sale && sale.receivable && sale.receivable.gratuityEnabled;
  },
  idSelector(sale: Sale) {
    return sale && sale.id;
  },
  txnDateSelector(sale: Sale) {
    return sale.txnDate;
  },
  dutyAmountSelector(sale: Sale) {
    return sale.duty && sale.duty.amount;
  },
  descriptionSelector(sale: Sale) {
    return sale.description;
  },
  amountSelector(sale: Sale) {
    return sale.amount;
  },
  achOnlineConvenienceFeeEnabledSelector(sale: Sale) {
    return sale?.convenienceFee?.ach?.enabled || false;
  },
  achOnlineConvenienceFeeAmountSelector(sale: Sale) {
    return sale?.convenienceFee?.ach?.feeAmount;
  },
  achOnlineConvenienceFeeAmountPaidSelector(sale: Sale) {
    return sale?.convenienceFee?.ach?.feeAmountPaid;
  },
  /**
   * Surcharge Painted Door Test.
   */
  surchargeExperimentEnabledSelector(sale: Sale) {
    return !!sale?.convenienceFee?.cc?.enabled;
  },
  surchargeExperimentBucketNameSelector(sale: Sale): string | undefined {
    return sale?.convenienceFee?.cc?.experimentBucketName;
  },
  surchargeFeeAmountSelector(sale: Sale): number | undefined {
    return sale?.convenienceFee?.cc?.feeAmount;
  },
  surchargeFeePercentageSelector(sale: Sale): string | undefined {
    return sale?.convenienceFee?.cc?.feePercentage;
  },
  surchargeTotalAmountWithFeeSelector(sale: Sale): number | undefined {
    return sale?.convenienceFee?.cc?.totalAmountWithFee;
  },
  surchargeSelector(sale: Sale): object | undefined {
    return sale?.convenienceFee?.cc;
  },
  achSelector(sale: Sale): object | undefined {
    return sale?.convenienceFee?.ach;
  },
  depositAmountSelector(sale: Sale) {
    return sale.requestedAmount;
  },
  balanceSelector(sale: Sale) {
    return sale && sale.receivable && sale.receivable.balance;
  },
  currencySelector(sale: Sale) {
    return sale && sale.currencyInfo && sale.currencyInfo.currency;
  },
  referenceNumberSelector(sale: Sale) {
    return sale.referenceNumber;
  },
  paymentDetailsMessageSelector(sale: Sale) {
    return sale.paymentDetailMessage;
  },
  dueDateSelector(sale: Sale) {
    return sale && sale.receivable && sale.receivable.dueDate;
  },
  isOverdueSelector(sale: Sale) {
    const dueDate = sale?.receivable?.dueDate;
    if (!dueDate) return false;

    const due = new Date(dueDate);
    const today = new Date();

    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return due < today;
  },
  typeSelector(sale: Pick<Sale, 'type'>) {
    return sale.type;
  },
  subTypeSelector(sale: Sale) {
    return sale.subType;
  },
  customerNameSelector(sale: Sale) {
    return sale?.contact?.displayName;
  },
  customerLocalNameIdSelector(sale: Sale): string | void {
    if (
      sale &&
      sale.contact &&
      sale.contact.entity &&
      sale.contact.entity.globalId &&
      sale.contact.entity.globalId.localId
    ) {
      return sale.contact.entity.globalId.localId;
    }
  },
  isScheduledPayment(sale: Sale) {
    return sale.scheduleInfo && sale.scheduleInfo.scheduleId;
  },
  isSchedulePay(sale: Sale) {
    return sale.scheduleInfo && sale.scheduleInfo.scheduleType === 'SCHEDULE_PAY';
  },
  isAutoPay(sale: Sale) {
    return sale.scheduleInfo && sale.scheduleInfo.scheduleType === 'AUTO_PAY';
  },
  isAutoPayEligible(sale: Sale) {
    return sale.scheduleInfo?.autoPayEligible;
  },
  /**
   * Determines if batch processing screen should be shown
   * Shows the screen when we have batch processing state (batchId and totalInvoices)
   */
  shouldShowBatchProcessingScreen(sale: Sale, _featureFlags: Record<string, any>): boolean {
    // Show batch processing screen if we have batchProcessing state with batchId
    return !!sale?.batchProcessing?.batchId;
  },
  /**
   * Determines if batch payment complete screen should be displayed
   * Shows the screen when we have batch payment complete data
   */
  shouldShowBatchPaymentCompleteScreen(sale: Sale, _featureFlags: Record<string, any>): boolean {
    return !!sale?.batchPaymentComplete;
  },
  categorizeInvoicesByPaymentMethod(invoices: UnpaidInvoice[]) {
    const bankOrCardInvoices: UnpaidInvoice[] = [];
    const bankOnlyInvoices: UnpaidInvoice[] = [];
    const cardOnlyInvoices: UnpaidInvoice[] = [];

    invoices.forEach((invoice) => {
      const methods = invoice.paymentMethods || [];
      const hasCard = methods.includes('Card') || methods.includes('CC');
      const hasBank =
        methods.includes('Bank') || methods.includes('ACH') || methods.includes('EFT'); // CA uses EFT for bank

      if (hasCard && hasBank) {
        bankOrCardInvoices.push(invoice);
      } else if (hasBank && !hasCard) {
        bankOnlyInvoices.push(invoice);
      } else if (hasCard && !hasBank) {
        cardOnlyInvoices.push(invoice);
      }
    });

    return { bankOrCardInvoices, bankOnlyInvoices, cardOnlyInvoices };
  },
  isCategoryAllChecked(categoryInvoices: UnpaidInvoice[], maxBalance: number = 100000) {
    const enabledInvoices = categoryInvoices.filter((invoice) => invoice.balance <= maxBalance);
    return enabledInvoices.length > 0 && enabledInvoices.every((invoice) => invoice.selected);
  },
  shouldShowCategoryToggleButton(invoices: UnpaidInvoice[], threshold: number = 5) {
    return invoices.length > threshold;
  },
  isSubscription(sale: Sale) {
    return sale.type === 'SUBSCRIPTION';
  },
  isEstimate(sale: Sale) {
    return sale.type === TXN_MAP.TYPES.ESTIMATE;
  },
  isGpu(sale: Sale) {
    return sale.isGpu;
  },
  getScheduleDate(sale: Sale) {
    const { scheduleInfo } = sale;
    return scheduleInfo && scheduleInfo.scheduleDate && new Date(scheduleInfo.scheduleDate);
  },
  getScheduleNextPaymentDate(sale: Sale) {
    const { scheduleInfo } = sale;
    return scheduleInfo && scheduleInfo.nextPaymentDate && new Date(scheduleInfo.nextPaymentDate);
  },
  getScheduleInfo(sale: Sale) {
    return sale.scheduleInfo;
  },
  getScheduleId(sale: Sale) {
    return sale.scheduleInfo?.scheduleId;
  },
  getAutoPayType(sale: Sale) {
    return sale.scheduleInfo?.autoPayType;
  },
  getScheduleTemplateId(sale: Sale) {
    return sale.scheduleInfo?.scheduleTemplateId;
  },
  getRecurringInfo(sale: Sale) {
    return sale.recurringInfo;
  },
  estimateStatus(sale: Sale) {
    if (sale.acceptStatusInfo && sale.acceptStatusInfo.acceptStatus) {
      return sale.acceptStatusInfo.acceptStatus;
    }
    return sale.status;
  },
  estimateActionDate(sale: Sale) {
    if (sale.acceptStatusInfo && sale.acceptStatusInfo.acceptDate) {
      return sale.acceptStatusInfo.acceptDate;
    }
    return null;
  },
  lineItemsSelector(sale: Sale) {
    return sale.lines;
  },
  toEmailsSelector(sale: Pick<Sale, 'contact'>) {
    return sale?.contact?.toEmails || [];
  },
  taxAmountSelector(sale: Sale) {
    if (sale.tax && typeof sale.tax.totalTaxAmount === 'number') {
      return sale.tax.totalTaxAmount;
    }
    return 0;
  },
  customerOpenInvoicesSelector(sale: Sale) {
    return sale.customerOpenInvoices;
  },
  isOneToManyPaymentRequest(sale: Sale) {
    return (
      sale.type === TXN_MAP.TYPES.PAYMENT_REQUEST &&
      sale.subType === TXN_MAP.SUB_TYPES.PAYMENT_LINK_MULTIPLE
    );
  },
  estimatedDeliverySelector(sale: Sale) {
    return sale.estimatedDelivery;
  },
  customerJobIdSelector(sale: Sale) {
    if (sale && sale._customerJobId) {
      return sale._customerJobId;
    }
    return null;
  },
  customerJobNameSelector(sale: Sale) {
    if (sale && sale._customerJobName) {
      return sale._customerJobName;
    }
    return null;
  },
  subscriptionInfo(sale: Sale) {
    return (
      (sale.subscriptionInfo && sale.subscriptionInfo.subscriptionInfo) ||
      ({} as SubscriptionInfo['subscriptionInfo'])
    );
  },
  oneTimeFeeLines(sale: Sale) {
    return sale.subscriptionInfo?.oneTimeFeeLines ?? [];
  },
  oneTimeFeeEnabled(sale: Sale) {
    return sale?.subscriptionInfo?.oneTimeFeeEnabled ?? false;
  },
  linesWithoutOTF(sale: Sale) {
    if (
      !Array.isArray(sale.lines) ||
      !sale.lines?.every((line) => typeof line === 'object' && line !== null)
    ) {
      return [];
    }
    return sale.lines?.filter(
      ({ type, item, sequence, amount }) =>
        type === SALE_MAP.LINE_ITEM_TYPE.SALES_ITEM_LINE_DETAIL &&
        item &&
        item?.name &&
        !!amount &&
        amount > 0 &&
        (!sale.subscriptionInfo?.oneTimeFeeEnabled ||
          (sequence && !sale.subscriptionInfo?.oneTimeFeeLines?.includes(sequence))) // filtering out the OTF
    ) as SaleLine[];
  },
  onlyOTFLines(sale: Sale) {
    if (
      !Array.isArray(sale.lines) ||
      !sale.lines?.every((line) => typeof line === 'object' && line !== null)
    ) {
      return [];
    }
    return sale.subscriptionInfo?.oneTimeFeeEnabled
      ? (sale.lines?.filter(
          ({ item, type, sequence, amount }) =>
            item &&
            item?.name &&
            !!amount &&
            amount > 0 &&
            type === SALE_MAP.LINE_ITEM_TYPE.SALES_ITEM_LINE_DETAIL &&
            sequence &&
            sale.subscriptionInfo?.oneTimeFeeLines?.includes(sequence)
        ) as SaleLine[])
      : [];
  },
  totalAmountWithoutOTF(sale: Sale, withoutTax?: boolean) {
    // OTF = One Time Fee (Subscriptions V1)
    if (!sale.lines) return sale.amount;

    const oneTimeFeeLines = sale.subscriptionInfo?.oneTimeFeeLines;
    if (!oneTimeFeeLines || oneTimeFeeLines.length === 0) return sale.amount;

    let totalAmountOTF = 0;
    let totalTaxAmountOTF = 0;

    let hasNonOTFLines = false; // Check if there are lines that are not OTF

    for (const line of sale.lines) {
      if (oneTimeFeeLines.includes(line.sequence)) {
        totalAmountOTF += line.amount;
        totalTaxAmountOTF += line.taxAmount ?? 0;
      } else {
        hasNonOTFLines = true;
      }
    }

    if (!hasNonOTFLines) return 0; // Return 0 if there are only OTF lines

    if (withoutTax) {
      const totalTaxAmount = sale.tax?.totalTaxAmount ?? 0;
      const totalAmountWithoutAnyTaxes = sale.amount - totalAmountOTF - totalTaxAmount;
      return Math.max(0, totalAmountWithoutAnyTaxes);
    }

    return Math.max(0, sale.amount - totalAmountOTF - totalTaxAmountOTF);
  },
  subscriptionStatus(sale: Sale) {
    return (
      sale.subscriptionInfo &&
      sale.subscriptionInfo.subscriptionInfo &&
      sale.subscriptionInfo.subscriptionInfo.status
    );
  },
  subscriptionPaymentInfo(sale: Sale) {
    return sale.subscriptionInfo && sale.subscriptionInfo.paymentInfo;
  },
  isSubscriptionActive(sale: Sale) {
    const { subscriptionInfo } = sale;
    if (subscriptionInfo && subscriptionInfo.subscriptionInfo) {
      const { status } = subscriptionInfo.subscriptionInfo;
      return (
        status === SUBSCRIPTION_STATUS_TYPES.ACTIVE || status === SUBSCRIPTION_STATUS_TYPES.ERROR
      );
    }
    return false;
  },
  isWalletUsedForActiveSubscription(sale: Sale, wallet: UserWallet): boolean | void {
    const paymentInfo = saleSelectors.subscriptionPaymentInfo(sale);

    if (paymentInfo && paymentInfo.walletInfo) {
      const {
        walletInfo: { token },
      } = paymentInfo;
      const { id } = wallet;
      return token === id;

      // That code for when we didnt had the token (wallet entry id) in the subscription info:
      // const decodeMaskedNumber = (masked, numOfDigits = 4) => {
      //   try {
      //     const [digits] = masked.match(/\d+/g);
      //     const firstLetter = masked.charAt(0);
      //     return `${firstLetter}${digits.slice(digits.length - numOfDigits)}`;
      //   } catch (e) {
      //     return false;
      //   }
      // };
      //
      // const { paymentMaskedCard } = paymentInfo;
      // const maskedNumber = getMaskedPaymentNumber(wallet);
      // const decodedCardInSubscription = decodeMaskedNumber(paymentMaskedCard);
      //
      // return (
      //   decodedCardInSubscription ===
      //   decodeMaskedNumber(maskedNumber, decodedCardInSubscription.length - 1)
      // );
    }
  },

  subscriptionChargeDate(sale: Sale) {
    const getNumberWithOrdinal = (n: number) => {
      let s = ['th', 'st', 'nd', 'rd'],
        v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    const { startDate = '', term } = saleSelectors.subscriptionInfo(sale);

    const intervalDay = parseInt(startDate.split('-')[2] || '1', 10);
    const ordinal = getNumberWithOrdinal(intervalDay);

    return {
      intervalDay,
      ordinal,
      term,
    };
  },
};

export const emailFromSaleSelector = createSelector(
  [(state: any) => saleSelectors.toEmailsSelector(state.sale)],
  (emails): string | undefined => emails?.[0]
);

export const isForcedConsentSelector = createSelector(
  [(state: any) => state.featureFlags, (state: any) => state.sale],
  (featureFlags, sale) => {
    const hasFeatureFlag = featureFlags?.['cp-recurring-invoice-forced-autopay'];
    const hasForcedConsent = saleSelectors.getAutoPayType(sale) === AUTOPAY_MAP.TYPE.FORCED_CONSENT;

    return Boolean(hasFeatureFlag && hasForcedConsent);
  }
);

export const isSurchargingEnabledSelector = createSelector(
  [(state: any) => state.featureFlags],
  (featureFlags) => Boolean(featureFlags?.[SURCHARGE_FF_KEY])
);

export const isOrphanedInvoiceSelector = createSelector(
  [isForcedConsentSelector, (state: any) => state.sale],
  (hasForcedConsent, sale) => {
    const isScheduleMissingButScheduleTemplateIdExists =
      saleSelectors.getScheduleId(sale) === null && !!saleSelectors.getScheduleTemplateId(sale);

    return Boolean(hasForcedConsent && isScheduleMissingButScheduleTemplateIdExists);
  }
);

export const isForcedAutopayRecurringSelector = createSelector(
  [isForcedConsentSelector, isOrphanedInvoiceSelector, (state: any) => state.sale],
  (hasForcedConsent, isOrphanedInvoice, sale): boolean => {
    const hasRecurringInfo = !!saleSelectors.getRecurringInfo(sale);
    const isAutoPayEligible = !!saleSelectors.isAutoPayEligible(sale);

    return hasForcedConsent && hasRecurringInfo && isAutoPayEligible && !isOrphanedInvoice;
  }
);

export const forcedAutopayStartDateSelector = createSelector(
  [(state: any) => state.sale],
  (sale): Date => {
    const today = new Date();
    const recurringInfo = saleSelectors.getRecurringInfo(sale);

    if (recurringInfo?.startDate) {
      const startDateObj = new Date(recurringInfo.startDate);
      if (startDateObj > today) {
        return startDateObj;
      }
    }

    return today;
  }
);

export const nextPaymentDateSelector = createSelector(
  [(state: any) => state.sale],
  (sale): string | null => {
    const nextPaymentDate = saleSelectors.getScheduleNextPaymentDate(sale);

    if (nextPaymentDate) {
      return nextPaymentDate.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      });
    }

    return null;
  }
);

export const autoPayIntervalSelector = createSelector(
  [(state: any) => state.sale],
  (sale): string => {
    const recurringInfo = saleSelectors.getRecurringInfo(sale);
    return recurringInfo?.recurringDescription?.toLowerCase?.() || 'monthly';
  }
);

function localIdSelector(sale: Sale): Sale['entity']['globalId']['localId'] | void {
  if (sale && sale.entity && sale.entity.globalId && sale.entity.globalId.localId) {
    return sale.entity.globalId.localId;
  }
}
